import React, { useState, useEffect } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import RepositorySelector from "./RepositorySelector";
import { Shield, Github, Link, Lock } from "lucide-react";
import { useScan } from "../contexts/ScanContext";
import securityApi from "../api/securityApi";

const ConnectRepository = () => {
  const [searchParams] = useSearchParams();
  // Default to the GitHub flow; `?method=direct` opts into pasting a URL.
  const [inputMethod, setInputMethod] = useState(
    searchParams.get("method") === "direct" ? "direct" : "select"
  );
  const [repoUrl, setRepoUrl] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const { startScan, isGuest, error: scanError, setError: setScanError } = useScan();
  const [error, setError] = useState(null);
  // Without a login only a public repository URL can be scanned.
  const method = isGuest ? "direct" : inputMethod;
  const [isLoading, setIsLoading] = useState(false); // Added for loading state

  // An error left by an earlier scan belongs to that scan, not to this form.
  useEffect(() => {
    setScanError(null);
  }, [setScanError]);

  const handleDirectSubmit = async (e) => {
    e.preventDefault();
    setError(null); // Clear previous errors

    if (!repoUrl.trim()) {
      setError("Please enter a repository URL");
      return;
    }

    // check for basic GitHub URL structure
    const githubRepoUrlPattern =
      /^(https?:\/\/)?(www\.)?github\.com\/[^\/\s]+\/[^\/\s]+$/;
    if (!githubRepoUrlPattern.test(repoUrl)) {
      setError(
        "Please enter a valid GitHub repository URL (e.g., https://github.com/username/repository)"
      );
      return;
    }

    setIsLoading(true);

    try {
      // Guests skip the pre-check, which needs a session; the public scan endpoint validates.
      const validationData = isGuest
        ? { status: "VALID" }
        : await securityApi.validateRepository(repoUrl, isPrivate);

      if (validationData.status === "NOT_FOUND") {
        setError(
          validationData.message ||
            "Repository not found or inaccessible. Please check the URL."
        );
        setIsLoading(false);
        return;
      }
      if (validationData.status === "PRIVATE_NEEDS_CHECKBOX") {
        if (isGuest) {
          setError("This repository is private. Sign in with GitHub to scan private repositories.");
          setIsLoading(false);
          return;
        }
        setError(
          validationData.message ||
            'This repository is private. Please check the "This is a private repository" box.'
        );
        setIsLoading(false);
        return;
      }
      // This handles API_ERROR that might still come with a 200 OK
      // or if the backend logic changes to send more 200 OKs with error statuses.
      if (validationData.status === "API_ERROR") {
        setError(
          validationData.message ||
            "A validation API error occurred. Please try again."
        );
        setIsLoading(false);
        return;
      }

      // If the status is not explicitly handled above and is not VALID, it's an unexpected 2xx state.
      if (validationData.status !== "VALID") {
        setError(
          validationData.message ||
            "An unexpected validation issue occurred. Please check the URL and try again."
        );
        setIsLoading(false);
        return;
      }

      // If status is VALID
      await startScan(repoUrl, isPrivate);
      setError(null);
    } catch (err) {
      if (err && err.message) {
        setError(err.message);
      } else if (typeof err === "string") {
        setError(err);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8 flex flex-col items-center justify-center">
      <div className="max-w-2xl w-full bg-white border border-gray-200 rounded-xl p-4 sm:p-6 lg:p-8 shadow-sm">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
          {isGuest ? "Scan a Public Repository" : "Connect Your Repository"}
        </h2>

        {/* Toggle between selection methods (selecting from GitHub needs a login) */}
        {!isGuest && (
        <div className="flex justify-center mb-6 w-full">
          <div className="bg-gray-100 p-1 rounded-lg flex flex-col w-full sm:flex-row sm:w-auto">
            <button
              onClick={() => setInputMethod("select")}
              className={`w-full sm:w-auto justify-center px-4 py-2 min-h-[44px] rounded-lg flex items-center ${
                inputMethod === "select"
                  ? "bg-teal-600 text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <Github className="w-4 h-4 mr-2" />
              Select Repository
            </button>
            <button
              onClick={() => setInputMethod("direct")}
              className={`w-full sm:w-auto justify-center px-4 py-2 min-h-[44px] rounded-lg flex items-center ${
                inputMethod === "direct"
                  ? "bg-teal-600 text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <Link className="w-4 h-4 mr-2" />
              Enter URL Directly
            </button>
          </div>
        </div>
        )}

        {method === "select" ? (
          <RepositorySelector />
        ) : (
          <div>
            <p className="text-gray-600 mb-6 text-center">
              Enter the URL of any public GitHub repository
            </p>

            <form onSubmit={handleDirectSubmit}>
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="https://github.com/username/repository"
                  value={repoUrl}
                  onChange={(e) => setRepoUrl(e.target.value)}
                  className="w-full bg-gray-50 text-gray-900 border border-gray-300 rounded-lg p-3 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                  disabled={isLoading} // Disable input while loading
                />
                {(error || scanError) && (
                  <p className="text-red-600 mt-2 text-sm">{error || scanError}</p>
                )}
              </div>

              {/* <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="isPrivate"
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  className="mr-2 h-4 w-4 rounded border-gray-300 bg-gray-50 text-teal-600 focus:ring-teal-500"
                  disabled={isLoading} // Disable checkbox while loading
                />
                <label
                  htmlFor="isPrivate"
                  className="flex items-center text-gray-600"
                >
                  <Lock className="h-4 w-4 mr-1" />
                  This is a private repository
                </label>
              </div> */}

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="w-full sm:w-auto justify-center px-6 py-3 min-h-[44px] rounded-lg flex items-center space-x-2 bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50"
                  disabled={isLoading} // Disable button while loading
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Validating & Connecting...
                    </>
                  ) : (
                    <>
                      <Shield className="h-5 w-5" />
                      <span>Start Scan</span>
                    </>
                  )}
                </button>
              </div>
            </form>

            {isGuest && (
              <p className="text-gray-500 mt-6 text-center text-sm">
                No account needed for public repositories.{" "}
                <RouterLink to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
                  Sign in
                </RouterLink>{" "}
                to scan private repositories, auto-fix findings and keep your scan history.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectRepository;
